import { TriangleAlert } from "lucide-react";
import { AlertDialog as AD } from "radix-ui";
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Button } from "./button";
import { Input } from "./input";

export interface ConfirmOptions {
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` = destructive (coral button + warning icon). */
  tone?: "default" | "danger";
  /** Require typing this exact text to enable the confirm button (irreversible actions). */
  confirmText?: string;
  icon?: ReactNode;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;
const ConfirmContext = createContext<ConfirmFn | null>(null);

/**
 *   const confirm = useConfirm();
 *   if (await confirm({ title: "Remove Priya?", description: "…", confirmLabel: "Remove", tone: "danger" })) remove();
 * Prefer toasts with Undo for reversible actions; confirm only what cannot be undone.
 */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<(ConfirmOptions & { open: boolean }) | null>(null);
  const [typed, setTyped] = useState("");
  const resolver = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    resolver.current?.(false);
    setTyped("");
    setState({ ...options, open: true });
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const close = (result: boolean) => {
    resolver.current?.(result);
    resolver.current = null;
    setState((s) => (s ? { ...s, open: false } : s));
  };

  const danger = state?.tone === "danger";
  const blocked = !!state?.confirmText && typed.trim() !== state.confirmText;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AD.Root open={!!state?.open} onOpenChange={(o) => !o && close(false)}>
        <AD.Portal>
          <AD.Overlay className="fixed inset-0 z-50 bg-overlay backdrop-blur-[2px] data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out" />
          <AD.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-[440px] -translate-x-1/2 -translate-y-1/2 rounded-panel border border-line bg-surface p-6 shadow-lg outline-none data-[state=open]:animate-dialog-in data-[state=closed]:animate-dialog-out">
            <div className="flex gap-4">
              {(danger || state?.icon) && (
                <div className={cn("flex size-10 shrink-0 items-center justify-center rounded-full [&_svg]:size-5", danger ? "bg-danger-soft text-danger-ink" : "bg-primary-soft text-primary-ink")}>
                  {state?.icon ?? <TriangleAlert />}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <AD.Title className="font-display text-lg font-semibold tracking-tight">{state?.title}</AD.Title>
                {state?.description ? (
                  <AD.Description className="mt-1.5 text-base leading-relaxed text-fg-muted">{state.description}</AD.Description>
                ) : (
                  <AD.Description className="sr-only">Please confirm</AD.Description>
                )}
                {state?.confirmText && (
                  <label className="mt-4 grid gap-1.5 text-sm font-semibold text-fg-soft">
                    <span>
                      Type <strong className="font-extrabold text-fg">{state.confirmText}</strong> to confirm
                    </span>
                    <Input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" autoFocus />
                  </label>
                )}
              </div>
            </div>
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <AD.Cancel asChild>
                <Button variant="secondary">{state?.cancelLabel ?? "Cancel"}</Button>
              </AD.Cancel>
              <AD.Action asChild>
                <Button
                  variant={danger ? "danger" : "primary"}
                  disabled={blocked}
                  onClick={(e) => {
                    e.preventDefault();
                    if (!blocked) close(true);
                  }}
                >
                  {state?.confirmLabel ?? "Confirm"}
                </Button>
              </AD.Action>
            </div>
          </AD.Content>
        </AD.Portal>
      </AD.Root>
    </ConfirmContext.Provider>
  );
}
